import {
  AddRounded,
  CloseRounded,
  TuneRounded,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SwayyApiError } from "../../../api/SwayyProductsAPI";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { forceLogout } from "../../../../../api/AuthSession";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import {
  SwayyProduct,
  SwayyProductAction,
  SwayyProductFilters as SwayyProductFilterState,
  SwayyProductSort,
  SwayyProductStatus,
  SwayySalesChannel,
} from "../../../models/products/SwayyProduct";
import { SwayyProductDetailTab } from "../../../models/products/SwayyProductDetail";
import { SwayyProductsStore } from "../../../stores/SwayyProductsStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import SwayyProductFilters from "../../components/products/SwayyProductFilters";
import SwayyProductsListing from "../../components/products/SwayyProductsListing";
import { SwayyPageHeader } from "../../components/SwayyPageShell";

const PRODUCT_STATUSES: SwayyProductStatus[] = ["draft", "live", "archived"];
const PRODUCT_SORTS: SwayyProductSort[] = ["newest", "name", "updated"];
const PAGE_SIZES = [10, 30, 50, 100];

const parseNumericParameter = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const getInitialListingState = (search: string) => {
  const params = new URLSearchParams(search);
  const status = params.get("status") as SwayyProductStatus | null;
  const sortBy = params.get("sort") as SwayyProductSort | null;
  const requestedLimit = Number(params.get("limit"));
  const requestedOffset = Number(params.get("offset"));
  const channels: SwayySalesChannel[] = [];
  if (params.get("is_b2c") === "true") channels.push("B2C");
  if (params.get("is_b2b") === "true") channels.push("B2B");

  const filters: SwayyProductFilterState = {
    search: params.get("q") || "",
    status: status && PRODUCT_STATUSES.includes(status) ? status : "live",
    brandId: parseNumericParameter(params.get("brand_id")),
    categoryId: parseNumericParameter(params.get("category_id")),
    channels,
    sortBy: sortBy && PRODUCT_SORTS.includes(sortBy) ? sortBy : "newest",
  };

  return {
    filters,
    pageSize: PAGE_SIZES.includes(requestedLimit) ? requestedLimit : 30,
    offset:
      Number.isInteger(requestedOffset) && requestedOffset >= 0
        ? requestedOffset
        : 0,
  };
};

const SwayyProducts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const initialListingState = useRef(getInitialListingState(location.search));
  const storeRef = useRef<SwayyProductsStore | null>(null);
  const handlingUnauthorized = useRef(false);
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  if (!storeRef.current) {
    storeRef.current = new SwayyProductsStore();
    storeRef.current.applyUrlParams(
      initialListingState.current.filters,
      initialListingState.current.pageSize,
      initialListingState.current.offset,
    );
  }

  const store = storeRef.current;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<{
    product?: SwayyProduct;
    action: "publish" | "archive" | "delete";
  } | null>(null);
  const debouncedSearch = useDebounce(store.searchDraft, 400);
  const productRequestKey = JSON.stringify(store.listQueryParams);

  const handleUnauthorized = useCallback(() => {
    if (handlingUnauthorized.current) return;
    handlingUnauthorized.current = true;
    void forceLogout();
  }, []);

  const handleApiError = useCallback(
    (error: SwayyApiError, message: string) => {
      if (error.status === 401) {
        handleUnauthorized();
        return;
      }
      showErrorRef.current(
        "Error",
        error.status === 403
          ? "You don’t have permission to view products."
          : message,
      );
    },
    [handleUnauthorized],
  );

  const loadProducts = useCallback(async () => {
    if (!canViewCatalog) return;
    const error = await store.loadProducts();
    if (!error) return;
    if (error.status === 401) {
      handleUnauthorized();
      return;
    }
    handleApiError(error, "Failed to load products.");
  }, [canViewCatalog, handleApiError, handleUnauthorized, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    void loadProducts();
  }, [canViewCatalog, loadProducts, productRequestKey]);

  useEffect(() => {
    if (!canViewCatalog) return;
    void store.loadFilterOptions().then((error) => {
      if (error) handleApiError(error, "Failed to load filters.");
    });
    return () => store.dispose();
  }, [canViewCatalog, handleApiError, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    store.setSearch(debouncedSearch);
  }, [canViewCatalog, debouncedSearch, store]);

  useEffect(() => {
    if (!canViewCatalog) return;
    const params = new URLSearchParams();
    const request = store.listQueryParams;
    if (request.q) params.set("q", request.q);
    params.set("status", request.status);
    if (request.brand_id !== undefined) {
      params.set("brand_id", String(request.brand_id));
    }
    if (request.category_id !== undefined) {
      params.set("category_id", String(request.category_id));
    }
    if (request.is_b2c) params.set("is_b2c", "true");
    if (request.is_b2b) params.set("is_b2b", "true");
    params.set("sort", request.sort);
    params.set("limit", String(request.limit));
    if (request.offset > 0) params.set("offset", String(request.offset));

    const nextSearch = `?${params.toString()}`;
    if (location.search !== nextSearch) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [canViewCatalog, location.search, navigate, productRequestKey, store]);

  const clearFilters = () => {
    store.clearFilters();
  };

  const openProductDetail = (
    product: SwayyProduct,
    tab?: SwayyProductDetailTab,
  ) => {
    navigate(`/products/${product.id}${tab && tab !== "info" ? `?tab=${tab}` : ""}`, {
      state: { fromSearch: location.search },
    });
  };

  const openAction = (
    product: SwayyProduct,
    action: SwayyProductAction,
  ) => {
    if (action === "view" || action === "edit") {
      openProductDetail(product);
      return;
    }
    if (action === "variants") {
      openProductDetail(product, "variants");
      return;
    }
    if (action === "images") {
      openProductDetail(product, "images");
      return;
    }
    if (action === "price-inventory") {
      openProductDetail(product, "variants");
      return;
    }
    if (
      action === "publish" ||
      action === "archive" ||
      action === "delete"
    ) {
      setDialogAction({ product, action });
    }
  };

  const isMutationAction = (action: "publish" | "archive" | "delete") =>
    ["publish", "archive", "delete"].includes(action);

  const getDialogContent = () => {
    if (!dialogAction) return { title: "", description: "" };
    const productName = dialogAction.product?.name ?? "product";
    const content: Record<
      "publish" | "archive" | "delete",
      { title: string; description: string }
    > = {
      publish: {
        title:
          dialogAction.product?.status === "archived"
            ? "Publish product again?"
            : "Publish product?",
        description: `${productName} will be set to Live.`,
      },
      archive: {
        title: "Archive product?",
        description: `${productName} will be archived.`,
      },
      delete: {
        title: "Delete product?",
        description: `${productName} will be permanently removed.`,
      },
    };

    return content[dialogAction.action];
  };

  const confirmAction = async () => {
    if (!dialogAction?.product) {
      setDialogAction(null);
      return;
    }

    try {
      const action = dialogAction.action;
      const productId = dialogAction.product.id;
      const error =
        action === "delete"
          ? await store.deleteProduct(productId)
          : action === "publish"
            ? await store.changeProductStatus(productId, "live")
            : action === "archive"
              ? await store.changeProductStatus(productId, "archived")
              : null;

      if (error) {
        handleApiError(error, "Failed to update product.");
        return;
      }

      setDialogAction(null);
      if (action === "delete" && store.products.length === 1 && store.page > 1) {
        store.setPage(store.page - 1);
      } else {
        await loadProducts();
      }
      showSuccess(
        action === "delete"
          ? "Product deleted."
          : action === "archive"
            ? "Product archived."
            : action === "publish"
              ? "Product published."
              : "Done.",
      );
    } catch {
      showError("Error", "Failed to update product.");
    }
  };

  const dialogContent = getDialogContent();
  const filtersDisabled =
    store.loadState === "idle" || store.loadState === "loading";

  if (!canViewCatalog) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ p: { xs: 0.5, md: 1 }, minWidth: 0 }}>
      <Grid2
        container
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Grid2 size={{ xs: 12, sm: "grow" }}>
          <SwayyPageHeader
            title="Products"
            description="Manage products, variants, pricing and inventory"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: "auto" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
              sx={{ textAlign: { xs: "left", sm: "right" } }}
            >
              {store.totalResults} products
            </CustomTypography>
            {canEditCatalog && (
              <AppButton
                label="Add Product"
                startIcon={<AddRounded />}
                onClick={() =>
                  navigate("/products/new", {
                    state: { fromSearch: location.search },
                  })
                }
                customStyles={{
                  width: { xs: "100%", sm: "auto" },
                  color: swayyColors.white,
                  backgroundColor: swayyColors.pine,
                  borderRadius: swayyRadii.md,
                  "&:hover": { backgroundColor: swayyColors.pineHover },
                }}
              />
            )}
          </Stack>
        </Grid2>
      </Grid2>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "block" },
          p: 2,
          mb: 2,
          borderColor: swayyColors.border,
          borderRadius: swayyRadii.lg,
          backgroundColor: swayyColors.white,
        }}
      >
        <SwayyProductFilters
          store={store}
          searchValue={store.searchDraft}
          onSearchChange={store.setSearchDraft}
          onClearFilters={clearFilters}
          disabled={filtersDisabled}
        />
      </Paper>

      <Box sx={{ display: { xs: "block", sm: "none" }, mb: 1.5 }}>
        <AppButton
          label={store.hasActiveFilters ? "Filters applied" : "Filters"}
          startIcon={<TuneRounded />}
          onClick={() => setFiltersOpen(true)}
          disabled={filtersDisabled}
          customStyles={{
            width: "100%",
            color: swayyColors.deepGreen,
            backgroundColor: swayyColors.white,
            border: `1px solid ${swayyColors.border}`,
            borderRadius: swayyRadii.md,
            "&:hover": { backgroundColor: swayyColors.pineLight },
          }}
        />
      </Box>

      <SwayyProductsListing
        store={store}
        onClearFilters={clearFilters}
        onRetry={() => void loadProducts()}
        onAction={openAction}
        onOpenProduct={(product: SwayyProduct) => openProductDetail(product as SwayyProduct)}
      />

      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: "min(90vw, 360px)",
              borderRadius: `${swayyRadii.xl} 0 0 ${swayyRadii.xl}`,
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <CustomTypography variant="heading5" color={swayyColors.deepGreen}>
            Filter products
          </CustomTypography>
          <IconButton
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
            sx={{ color: swayyColors.deepGreen }}
          >
            <CloseRounded />
          </IconButton>
        </Stack>
        <Divider sx={{ borderColor: swayyColors.border }} />
        <Box sx={{ p: 2 }}>
          <SwayyProductFilters
            store={store}
            searchValue={store.searchDraft}
            onSearchChange={store.setSearchDraft}
            onClearFilters={clearFilters}
            disabled={filtersDisabled}
            compact
          />
          <AppButton
            label="Show results"
            onClick={() => setFiltersOpen(false)}
            customStyles={{
              width: "100%",
              mt: 2,
              color: swayyColors.white,
              backgroundColor: swayyColors.pine,
              borderRadius: swayyRadii.md,
              "&:hover": { backgroundColor: swayyColors.pineHover },
            }}
          />
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(dialogAction)}
        onClose={() => setDialogAction(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: { sx: { borderRadius: swayyRadii.xl, p: 1 } },
        }}
      >
        <DialogTitle
          sx={{ color: swayyColors.deepGreen, fontWeight: 700, pb: 1 }}
        >
          {dialogContent.title}
        </DialogTitle>
        <DialogContent>
          <CustomTypography variant="body1" color={swayyColors.textSecondary}>
            {dialogContent.description}
          </CustomTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <AppButton
            label={
              dialogAction && isMutationAction(dialogAction.action)
                ? "Cancel"
                : "Close"
            }
            onClick={() => setDialogAction(null)}
            disabled={store.isUpdating}
            customStyles={{
              color: swayyColors.deepGreen,
              border: `1px solid ${swayyColors.border}`,
              borderRadius: swayyRadii.md,
              "&:hover": { backgroundColor: swayyColors.pineLight },
            }}
          />
          {dialogAction && isMutationAction(dialogAction.action) && (
            <AppButton
              label={
                dialogAction.action === "delete"
                  ? "Delete"
                  : dialogAction.action.charAt(0).toUpperCase() +
                    dialogAction.action.slice(1)
              }
              onClick={() => void confirmAction()}
              disabled={store.isUpdating}
              customStyles={{
                color: swayyColors.white,
                backgroundColor:
                  dialogAction.action === "delete"
                    ? swayyColors.danger
                    : swayyColors.pine,
                borderRadius: swayyRadii.md,
                "&:hover": {
                  backgroundColor:
                    dialogAction.action === "delete"
                      ? swayyColors.danger
                      : swayyColors.pineHover,
                },
              }}
            />
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default observer(SwayyProducts);
